// @givmo/design-system — Menu / DropdownMenu (Base UI Menu).
//
// 03-3c overlay tier. TWO skins on one Base UI Menu:
//   - LEGACY (default) — the Flutter webapp dropdown port (`CPopUpMenuButton`/`CPopUpMenuItem`):
//     rounded-rect (all15) popup, black @ 8% border, soft shadow, items `text-s16 font-w600 text-gray`
//     with a `bg-blue20` highlight. Kept INTACT for donate/legacy consumers.
//   - SAAS (`saas` on Trigger/Content/Item/GroupLabel) — the fintech-grade dropdown the rebuilt
//     admin/super-admin portals + `RowActions` use: popup `rounded-lg border border-border-subtle
//     shadow-md bg-surface p-4`, items `rounded-md px-12 py-8 text-s14 font-w500 text-text`,
//     `data-[highlighted]:bg-surface-hover` and the house `FOCUS_RING`; the `danger` item is
//     `text-danger-600` + `data-[highlighted]:bg-danger-50`; the group label is the 11px uppercase
//     tertiary caption.
//
// ⚠ TWO KINDS OF ITEM. `Item` is a plain `menuitem` — an ACTION. `RadioItem` (inside `RadioGroup`)
// is a `menuitemradio` carrying `aria-checked` — one of a SET of mutually exclusive choices. If a
// menu marks a current row with a check glyph, it wants `RadioItem`: a glyph is `aria-hidden`
// decoration, so with `Item` the rows read identically in the accessibility tree and nothing says
// which one is current. Both kinds take their classes from the SAME two recipe functions, so a
// selected row can never look different from an unselected one by drift.
//
// ⚠ `saas` IS PER-ELEMENT, AND A HALF-DRESSED POPUP IS A REAL DEFECT, not a cosmetic one. A Content
// that opts in while its Items do not renders ONE popup mixing two systems — s14/w500 rows beside
// s16/w600 rows — and, worse, the legacy item has NO `focus-visible` style at all, so keyboard focus
// in that menu is invisible. Pass it on every element of a SaaS menu, label included.
//
// Built on @base-ui/react/menu, which wires the accessible roles (button → menu/menuitem), full
// keyboard navigation (arrow/Home/End/type-ahead), Escape + outside-click dismissal, focus trap, and
// focus restore to the trigger.
//
// a11y: <Menu.Trigger> carries `aria-haspopup="menu"`/`aria-expanded`; <Menu.Content> is
// role="menu"; <Menu.Item> is role="menuitem" with `data-highlighted`/`data-disabled` state.
// `testId` forwards to the trigger (Content/Item accept their own `testId`).
//
// ⚠ A menu is NAMED BY ITS TRIGGER — the name goes on <Menu.Trigger>, never on the popup. Base UI
// points the popup's `aria-labelledby` at the trigger element (the WAI-ARIA menu-button pattern), and
// `aria-labelledby` outranks `aria-label` in the accessible-name computation, so an `aria-label` on
// the popup is inert: it lands in the DOM and is never announced. <Menu.Content> therefore does not
// accept one — name the menu through the trigger's own text, or through `aria-label` on
// <Menu.Trigger> when the trigger is iconographic. Dialog, Popover and Sheet are the opposite: their
// `aria-label` IS the name when no `Title` renders.
import type { FocusEventHandler, PointerEventHandler, ReactElement, ReactNode } from 'react';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';

const MenuRoot = BaseMenu.Root;
const MenuGroup = BaseMenu.Group;

export interface MenuTriggerProps extends TestIdProps {
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Render the trigger as a different element (e.g. a <Button> or icon button). */
  render?: ReactElement;
  /**
   * SaaS surface: stand the legacy focus glow down, exactly as supplying `render` does.
   *
   * ⚠ A BARE SAAS TRIGGER HAD NO COMPLIANT FOCUS STATE. The glow is a shadow, and a shadow alone
   * composites far under the 3:1 SC 1.4.11 floor; a consumer that spelled the house pairing in its
   * own `className` then had TWO shadow utilities racing, resolved only by `cn`'s merge order. This
   * says the trigger is on the SaaS side and lets the consumer's {@link FOCUS_RING} stand alone.
   */
  saas?: boolean;
  'aria-label'?: string;
  /**
   * The pointer reaching the trigger, and the trigger taking focus — the moments a person reaches
   * for a menu before it opens, so a menu whose rows wait on a read can start it then (the consoles'
   * account menu, AS-WEB16). Forwarded to the trigger element like every other prop below; declared
   * here because this wrapper's type names what it accepts.
   */
  onPointerEnter?: PointerEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
}

function MenuTrigger({
  children,
  disabled,
  className,
  render,
  saas = false,
  testId,
  ...props
}: MenuTriggerProps) {
  return (
    <BaseMenu.Trigger
      disabled={disabled}
      // When a `render` element is supplied (e.g. an IconButton), it owns its own focus ring — don't
      // stack the legacy glow on top. Bare LEGACY triggers keep the legacy focus-glow affordance.
      className={cn(
        'outline-none',
        render == null && !saas && 'focus-visible:shadow-focus-glow',
        className,
      )}
      render={render}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </BaseMenu.Trigger>
  );
}

export interface MenuContentProps extends TestIdProps {
  children: ReactNode;
  /** Side of the trigger the popup is placed on (default 'bottom'). */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment relative to the trigger (default 'end' — kebab menus drop down-left in the kit). */
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** Use the SaaS popup skin (surface, hairline border, shadow-md, 4px padding). */
  saas?: boolean;
  className?: string;
}

function MenuContent({
  children,
  side = 'bottom',
  align = 'end',
  sideOffset = 4,
  saas = false,
  className,
  testId,
}: MenuContentProps) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        className="z-50 outline-none"
      >
        <BaseMenu.Popup
          className={cn(
            saas
              ? // SaaS: surface fill, hairline border, soft md shadow, tight 4px padding.
                'min-w-[180px] rounded-lg border border-border-subtle bg-surface p-4 shadow-md outline-none'
              : // Legacy kit: rounded-rect (all15) popup, black @ 8% border, soft shadow, white fill.
                'min-w-[180px] rounded-all15 border border-black/[0.08] bg-white py-8 shadow-soft outline-none',
            className,
          )}
          {...testIdProp(testId)}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

// ── The item recipes, extracted ────────────────────────────────────────────────────────────────
//
// ⚠ ONE SOURCE FOR EACH SKIN, because there are now TWO kinds of item — `Item` (menuitem) and
// `RadioItem` (menuitemradio) — and a selected organization must not look different from an
// unselected one just because the two recipes drifted. Same lesson as `FOCUS_RING`: a string copied
// to a second call site is a string that will diverge at the third.

function saasItemClasses(danger: boolean): string {
  return cn(
    'flex cursor-default items-center gap-8 rounded-md px-12 py-8',
    'font-sans text-s14 font-w500 tracking-givmo',
    // ⚠ THE RING ALONE WAS THIS ITEM'S WHOLE FOCUS STATE, at 1.23:1 — under the 3:1 SC 1.4.11
    // floor. `FOCUS_RING` adds the 2px solid outline, which is also the half that SURVIVES the
    // popup's clipping context where an outset shadow does not.
    'outline-none',
    FOCUS_RING,
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
    danger
      ? 'text-danger-600 data-[highlighted]:bg-danger-50'
      : 'text-text data-[highlighted]:bg-surface-hover',
  );
}

function legacyItemClasses(): string {
  return cn(
    'flex cursor-default items-center gap-8 px-18 py-10',
    'font-sans text-s16 font-w600 tracking-givmo text-gray',
    'data-[highlighted]:bg-blue20',
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
    'outline-none',
  );
}

export interface MenuItemProps extends TestIdProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Whether to close the menu when the item is clicked (default true). */
  closeOnClick?: boolean;
  /** Optional leading glyph. */
  startIcon?: ReactNode;
  /** Optional trailing glyph (Flutter CPopUpMenuItem puts the icon on the trailing edge). */
  endIcon?: ReactNode;
  /** Use the SaaS item skin (rounded-md row, 14px/medium text, surface-hover highlight). */
  saas?: boolean;
  /** Destructive (SaaS only): danger-600 text + danger-50 highlight. */
  tone?: 'default' | 'danger';
  className?: string;
}

function MenuItem({
  children,
  onClick,
  disabled,
  closeOnClick,
  startIcon,
  endIcon,
  saas = false,
  tone = 'default',
  className,
  testId,
}: MenuItemProps) {
  if (saas) {
    const danger = tone === 'danger';
    return (
      <BaseMenu.Item
        onClick={onClick}
        disabled={disabled}
        closeOnClick={closeOnClick}
        className={cn(saasItemClasses(danger), className)}
        {...testIdProp(testId)}
      >
        {startIcon && (
          <span className={cn('flex shrink-0', danger ? 'text-danger-600' : 'text-icon')}>
            {startIcon}
          </span>
        )}
        <span className="flex-1">{children}</span>
        {endIcon && <span className="flex shrink-0">{endIcon}</span>}
      </BaseMenu.Item>
    );
  }

  return (
    <BaseMenu.Item
      onClick={onClick}
      disabled={disabled}
      closeOnClick={closeOnClick}
      className={cn(legacyItemClasses(), className)}
      {...testIdProp(testId)}
    >
      {startIcon && <span className="flex shrink-0 text-gray80">{startIcon}</span>}
      <span className="flex-1">{children}</span>
      {endIcon && <span className="flex shrink-0 text-black">{endIcon}</span>}
    </BaseMenu.Item>
  );
}

// ── RadioGroup / RadioItem — a menu that expresses WHICH ONE is selected ───────────────────────
//
// ⚠ A CHECK GLYPH IS NOT AN ANSWER TO "WHICH ONE IS ACTIVE". The organization switcher marked its
// active row with a `Check` icon carrying `aria-hidden` on a plain `menuitem` — so in the
// accessibility tree the two rows read identically and, inside the menu, nothing said which
// organization you are in. Meaning-bearing imagery hidden as decorative is the SC 1.1.1 failure.
//
// `menuitemradio` + `aria-checked` is the standard mechanism for exactly this: a set of mutually
// exclusive options with one selected. It also gives assistive technology set position ("1 of 2"),
// which a visually-hidden "(current)" string never would. Base UI ships the parts; these wrap them
// in the SAME skins as `Item`, from the same recipe functions, so the row is pixel-identical.

export interface MenuRadioGroupProps {
  children: ReactNode;
  /** The selected value. Compared with each item's `value` to decide `aria-checked`. */
  value?: unknown;
  onValueChange?: (value: unknown) => void;
  className?: string;
}

function MenuRadioGroup({ children, value, onValueChange, className }: MenuRadioGroupProps) {
  return (
    <BaseMenu.RadioGroup
      value={value}
      onValueChange={onValueChange ? (next) => onValueChange(next) : undefined}
      className={className}
    >
      {children}
    </BaseMenu.RadioGroup>
  );
}

export interface MenuRadioItemProps extends Omit<MenuItemProps, 'tone'> {
  /** This row's identity. Selected when it equals the group's `value`. */
  value: unknown;
}

function MenuRadioItem({
  children,
  value,
  onClick,
  disabled,
  closeOnClick,
  startIcon,
  endIcon,
  saas = false,
  className,
  testId,
}: MenuRadioItemProps) {
  // Deliberately no `tone`: a destructive option is never one of a set of mutually exclusive
  // choices, so a `danger` radio row would be a shape this component should not make available.
  const body = (
    <>
      {startIcon && (
        <span className={cn('flex shrink-0', saas ? 'text-icon' : 'text-gray80')}>{startIcon}</span>
      )}
      <span className="flex-1">{children}</span>
      {endIcon && (
        <span className={cn('flex shrink-0', saas ? undefined : 'text-black')}>{endIcon}</span>
      )}
    </>
  );

  return (
    <BaseMenu.RadioItem
      value={value}
      onClick={onClick}
      disabled={disabled}
      closeOnClick={closeOnClick}
      className={cn(saas ? saasItemClasses(false) : legacyItemClasses(), className)}
      {...testIdProp(testId)}
    >
      {body}
    </BaseMenu.RadioItem>
  );
}

export interface MenuSeparatorProps {
  className?: string;
}

function MenuSeparator({ className }: MenuSeparatorProps) {
  return <BaseMenu.Separator className={cn('my-8 h-px bg-gray30', className)} />;
}

export interface MenuGroupLabelProps {
  children: ReactNode;
  /**
   * Use the SaaS caption skin.
   *
   * ⚠ THE LEGACY LABEL IS `gray60` (#9F9F9F) = **2.65:1** on the popup's white — under SC 1.4.3's
   * 4.5:1 for normal-size text, and 12px/w600 is nowhere near the large-text threshold. It had no
   * second variant, so every SaaS menu that grouped its items inherited it. The SaaS skin is the
   * estate's live caption role — 11px w600 uppercase tracked at `text-text-tertiary` (#697586,
   * **4.68:1**) — the same recipe the app switcher's own popup heading already ships.
   */
  saas?: boolean;
  className?: string;
}

function MenuGroupLabel({ children, saas = false, className }: MenuGroupLabelProps) {
  return (
    <BaseMenu.GroupLabel
      className={cn(
        saas
          ? // `px-12` aligns the caption with the SaaS item text above and below it.
            'px-12 pb-4 pt-8 font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary'
          : 'px-18 py-4 font-sans text-s12 font-w600 tracking-givmo text-gray60',
        className,
      )}
    >
      {children}
    </BaseMenu.GroupLabel>
  );
}

/** Compound Menu API: `<Menu.Root>…<Menu.Trigger>…<Menu.Content>…<Menu.Item>`. */
export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Content: MenuContent,
  Item: MenuItem,
  RadioGroup: MenuRadioGroup,
  RadioItem: MenuRadioItem,
  Separator: MenuSeparator,
  Group: MenuGroup,
  GroupLabel: MenuGroupLabel,
};
